import {
  Badge,
  Box,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useRecoilValue } from 'recoil';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { settingsAtom } from '../../atoms/Settings';
import { useThemeColor } from '../../hooks/useThemeColor';
import { SearchBox } from '../SearchBox/SearchBox';
import { sharedColors } from '../../utils/Style';
import { NavMenuItem } from '../../interfaces/Settings/NavMenuItem';
import { useClickMenuItem } from './useClickMenuItem';
import { useBasket } from '../../hooks/useBasket';
import { isTokenValid } from '../../utils/Auth';
import { OffersButton } from './OffersButton';
import { getSelectedLocale, setSelectedLocale } from '../../utils/Localization';
import { allowedLanguages } from '../../constants/Translation';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type MenuAccordionProps = {
  menuItem: NavMenuItem;
};

const SecondLevelAccordion = ({ menuItem }: MenuAccordionProps) => {
  const [open, setOpen] = useState(false);

  const settings = useRecoilValue(settingsAtom);

  const onClick = useClickMenuItem();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        onClick={() => {
          if (menuItem.items.length) {
            setOpen((x) => !x);
          } else {
            onClick(menuItem);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 2.5,
          py: 1,
          justifyContent: 'space-between',
          ...settings.settingsV2?.appHeaderMobileSettings?.secondLevelRowStyle,
        }}
      >
        <Typography
          onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            onClick(menuItem);
          }}
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: sharedColors.gray6,
            ...settings.settingsV2?.appHeaderMobileSettings
              ?.secondLevelTextStyle,
          }}
        >
          {menuItem.name}
        </Typography>

        {!!menuItem.items.length && (
          <IconButton
            size='small'
            sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
          >
            <ExpandMoreIcon
              sx={{
                ...settings.settingsV2?.appHeaderMobileSettings
                  ?.secondLevelChevronStyle,
              }}
            />
          </IconButton>
        )}
      </Box>

      {open &&
        menuItem.items.map((child, i) => (
          <Box
            key={i}
            sx={{
              px: 2.5,
              py: 1,
              ...settings.settingsV2?.appHeaderMobileSettings
                ?.thirdLevelRowStyle,
            }}
          >
            <Typography
              onClick={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();

                onClick(child);
              }}
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: sharedColors.gray6,
                ...settings.settingsV2?.appHeaderMobileSettings
                  ?.thirdLevelTextStyle,
              }}
            >
              {child.name}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};

const TopLevelAccordion = ({ menuItem }: MenuAccordionProps) => {
  const [open, setOpen] = useState(false);

  const settings = useRecoilValue(settingsAtom);

  const onClick = useClickMenuItem();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        onClick={() => {
          if (menuItem.items.length) {
            setOpen((x) => !x);
          } else {
            onClick(menuItem);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          justifyContent: 'space-between',
          ...settings.settingsV2?.appHeaderMobileSettings?.firstLevelRowStyle,
        }}
      >
        <Typography
          onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            onClick(menuItem);
          }}
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: sharedColors.gray7,
            ...settings.settingsV2?.appHeaderMobileSettings
              ?.firstLevelTextStyle,
          }}
        >
          {menuItem.name}
        </Typography>

        {!!menuItem.items.length && (
          <IconButton
            sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
          >
            <ExpandMoreIcon
              sx={{
                ...settings.settingsV2?.appHeaderMobileSettings
                  ?.firstLevelChevronStyle,
              }}
            />
          </IconButton>
        )}
      </Box>

      <Divider
        sx={{
          ...settings.settingsV2?.appHeaderMobileSettings
            ?.firstLevelDividerStyle,
        }}
      />

      {open &&
        menuItem.items.map((child, i) => (
          <SecondLevelAccordion key={i} menuItem={child} />
        ))}
    </Box>
  );
};

export const AppHeaderMobile = () => {
  const t = useLocalizedText();

  const settings = useRecoilValue(settingsAtom);
  const [showMenu, setShowMenu] = useState(false);

  const { primary, secondary } = useThemeColor();

  const { totalQuantity } = useBasket();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setShowMenu(false);
  }, [location.pathname]);

  const logo = settings.settingsV1.storefront_header_logo;
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          ...settings.settingsV2?.appHeaderMobileSettings?.rootStyle,
        }}
      >
        <IconButton onClick={() => setShowMenu((x) => !x)}>
          <MenuIcon
            sx={{
              ...settings.settingsV2?.appHeaderMobileSettings?.menuIconStyle,
            }}
          />
        </IconButton>

        {logo ? (
          <Box
            component='img'
            onClick={() => navigate('')}
            src={logo}
            sx={{
              height: 55,
              width: 'auto',
              ...settings.settingsV2?.appHeaderMobileSettings?.logoStyle,
            }}
          />
        ) : (
          <Typography>{settings.settingsV1.storefront_navbar_text}</Typography>
        )}

        <Box sx={{ ml: 'auto' }} />

        <Box>
          <IconButton onClick={() => navigate('/favoriler')}>
            <FavoriteBorderIcon
              sx={{
                ...settings.settingsV2?.appHeaderSettings?.iconStyle,
              }}
            />
          </IconButton>

          <IconButton onClick={() => navigate('/sepet')}>
            <Badge
              badgeContent={totalQuantity.toString()}
              sx={{
                '& .MuiBadge-badge': {
                  color: secondary,
                  backgroundColor: primary,
                  ...settings.settingsV2?.appHeaderSettings?.iconBadgeStyle,
                },
              }}
              overlap='circular'
              style={{ transform: 'translate(25px, -15px)' }}
            />
            <ShoppingBasketIcon
              sx={{
                ...settings.settingsV2?.appHeaderSettings?.iconStyle,
              }}
            />
          </IconButton>

          <OffersButton />

          <IconButton
            onClick={() => navigate(isTokenValid() ? '/account' : '/giris-yap')}
          >
            <PersonOutlineIcon
              sx={{
                ...settings.settingsV2?.appHeaderSettings?.iconStyle,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <Box
        p={1.5}
        sx={{
          backgroundColor: showMenu ? sharedColors.white : sharedColors.gray3,
        }}
      >
        <SearchBox fullWidth placeholder={t('header.type_to_search')} />
      </Box>

      {showMenu && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            ...settings.settingsV2?.appHeaderMobileSettings?.navMenuStyle,
          }}
        >
          {settings.menu.map((item, i) => (
            <TopLevelAccordion key={i} menuItem={item} />
          ))}

          {settings.availableLocales.length > 1 && (
            <>
              <Divider />

              <Select
                size='small'
                fullWidth
                value={getSelectedLocale()}
                onChange={(ev) => {
                  setSelectedLocale(ev.target.value);
                  window.location.reload();
                }}
                sx={{ mt: 1.5 }}
              >
                {settings.availableLocales.map((locale) => (
                  <MenuItem key={locale} value={locale}>
                    {allowedLanguages.find((lang) => lang.locale === locale)
                      ?.name ?? locale}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
